import React from "react";
import { Button } from "../ui/button";
import { AlertTriangle, ArrowLeft, ArrowRight, Bookmark, Check, ChevronLeft, Target, X } from "lucide-react";

const GOOD = ["strong", "low", "none", "high_fit"];
const MID = ["moderate", "medium", "partial"];
const tone = (rating) => {
  const r = String(rating || "").toLowerCase();
  if (GOOD.includes(r)) return "good";
  if (MID.includes(r)) return "mid";
  return "bad";
};
const RATING_STYLE = {
  good: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mid: "bg-amber-50 text-amber-700 border-amber-200",
  bad: "bg-rose-50 text-rose-600 border-rose-200",
};
const RATING_BAR = { good: "bg-emerald-500 w-full", mid: "bg-amber-500 w-3/5", bad: "bg-rose-400 w-1/4" };
const EFFORT = { low: "Low effort to test", medium: "Moderate effort to test", high: "High effort to test" };

export const money = (p) => (p?.amount ? `${p.currency === "USD" ? "$" : "₹"}${Number(p.amount).toLocaleString()}` : "Not set");

const Block = ({ label, children, accent = "#0284C7", testId }) => (
  <div className="accent-bar rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-4 pl-5" style={{ "--accent": accent }} data-testid={testId}>
    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
    <div className="mt-1.5 text-sm text-slate-700 leading-relaxed">{children}</div>
  </div>
);

const Bullets = ({ items, empty }) => (
  items?.length ? (
    <ul className="space-y-1.5">
      {items.map((x, i) => (
        <li key={i} className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" /><span>{x}</span></li>
      ))}
    </ul>
  ) : <p className="text-slate-400">{empty}</p>
);

const ScoreRing = ({ score }) => {
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const tone = pct >= 70 ? "#059669" : pct >= 50 ? "#D97706" : "#E11D48";
  return (
    <div className="relative h-20 w-20 shrink-0" data-testid="hypothesis-detail-score-ring">
      <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEF2F7" strokeWidth="3.5" />
        <circle cx="18" cy="18" r="15.5" fill="none" stroke={tone} strokeWidth="3.5" strokeLinecap="round"
                strokeDasharray={`${(pct / 100) * 97.4} 97.4`} style={{ transition: "stroke-dasharray 0.6s ease" }} />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="num-display text-xl leading-none">{pct}</span>
        <span className="text-[9px] text-slate-400 leading-none mt-0.5">of 100</span>
      </span>
    </div>
  );
};

export const HypothesisDetail = ({ h, archetype, index, total, onPrev, onNext, onBack, onAction, busy }) => {
  const primary = h.status === "primary";
  const dims = h.fit_view?.dimensions || [];

  return (
    <div className="rise-in space-y-4 pb-24 sm:pb-4" data-testid={`hypothesis-detail-${h.id}`}>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:text-sky-800" data-testid="hypothesis-detail-back-to-list">
        <ChevronLeft className="w-4 h-4" /> All {total} income routes
      </button>

      <header className="card-soft p-5 sm:p-6">
        <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-700">
              {archetype?.name || h.archetype_id}
            </span>
            <h2 className="mt-2.5 text-xl sm:text-2xl font-bold tracking-tight text-slate-900 break-words" data-testid="hypothesis-detail-title">{h.title}</h2>
            <p className="mt-1 text-xs text-slate-500">Hypothesis {index + 1} of {total} · built from your own profile, not market evidence</p>
            {primary && (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="hypothesis-detail-primary-pill">
                <Target className="w-3.5 h-3.5" /> Your primary direction
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 sm:flex-col sm:items-end">
            {h.fit_view?.score != null ? (
              <>
                <ScoreRing score={h.fit_view.score} />
                <p className="text-[10px] leading-tight text-slate-400 sm:text-right">{h.fit_view.label}<br />Planning indicator only</p>
              </>
            ) : <span className="text-[11px] text-slate-400">Qualitative fit only</span>}
          </div>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <Block label="What you could sell" testId="hypothesis-detail-offer">{h.offer || "—"}</Block>
        <Block label="Who could buy it" accent="#0D9488" testId="hypothesis-detail-customer">{h.target_customer || "—"}</Block>
        <Block label="Problem it solves" accent="#D97706" testId="hypothesis-detail-problem">{h.problem || "—"}</Block>
        <Block label="Outcome for them" accent="#059669" testId="hypothesis-detail-outcome">{h.outcome || "—"}</Block>
        <Block label="Possible format" accent="#4F46E5" testId="hypothesis-detail-format">{h.delivery_model || "—"}</Block>
        <Block label="Why your profile supports it" accent="#0284C7" testId="hypothesis-detail-support">{h.why_profile_supports || "—"}</Block>
      </div>

      <Block label="How to test it" accent="#0D9488" testId="hypothesis-detail-test">{h.how_to_test || "—"}</Block>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="hypothesis-detail-existing-proof">
          <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-700">Proof you already have</p>
          <div className="mt-2 text-sm text-slate-700"><Bullets items={h.existing_proof} empty="Not found in your profile." /></div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="hypothesis-detail-missing-proof">
          <p className="text-[11px] font-mono uppercase tracking-wider text-amber-700">Still needs proof</p>
          <div className="mt-2 text-sm text-slate-700"><Bullets items={h.missing_proof} empty="—" /></div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="hypothesis-detail-chips">
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">{EFFORT[h.effort_to_test] || h.effort_to_test}</span>
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">Price hypothesis: <span className="num-display text-xs">{money(h.price_hypothesis)}</span></span>
        {(h.validation_channels || []).slice(0, 3).map((c) => (
          <span key={c} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700">Test via {c}</span>
        ))}
      </div>

      {!!h.constraint_conflicts?.length && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="hypothesis-detail-conflicts">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800"><AlertTriangle className="w-4 h-4" /> Conflicts with what you told us</p>
          <ul className="mt-2 space-y-1.5 text-sm text-amber-800">
            {h.constraint_conflicts.map((c, i) => (
              <li key={i} className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" /><span><span className="font-medium">{String(c.constraint || "").replace(/_/g, " ")}</span>: {c.issue}</span></li>
            ))}
          </ul>
        </div>
      )}

      {!!dims.length && (
        <section className="card-soft p-5" data-testid="hypothesis-detail-scorecard">
          <h3 className="font-bold text-slate-900">How this fit score is built</h3>
          <p className="text-xs text-slate-500 mt-1">Ten dimensions, each rated against your own profile and constraints.</p>
          <ul className="mt-4 divide-y divide-slate-100">
            {dims.map((d) => (
              <li key={d.key} className="py-3 first:pt-0 last:pb-0" data-testid={`hypothesis-detail-dimension-${d.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-800">{d.label}</p>
                  <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${RATING_STYLE[tone(d.rating)]}`}>{d.rating}</span>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100">
                  <div className={`h-1.5 rounded-full ${RATING_BAR[tone(d.rating)]}`} />
                </div>
                {d.note && <p className="mt-2 text-xs text-slate-500 leading-relaxed">{d.note}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 sm:static sm:rounded-2xl sm:border sm:bg-white sm:px-5 sm:shadow-sm" data-testid="hypothesis-detail-actions">
        <div className="mx-auto flex max-w-5xl flex-col gap-2.5">
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" onClick={onPrev} disabled={index === 0} className="h-10 border-slate-200 text-slate-600 disabled:opacity-40" data-testid="hypothesis-detail-prev-button">
              <ArrowLeft className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Back</span>
            </Button>
            <span className="num-display text-xs text-slate-500">{index + 1} / {total}</span>
            <Button variant="outline" onClick={onNext} disabled={index === total - 1} className="h-10 border-slate-200 text-slate-600 disabled:opacity-40" data-testid="hypothesis-detail-next-button">
              <span className="hidden sm:inline">Next</span> <ArrowRight className="w-4 h-4 sm:ml-1.5" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Button disabled={busy || primary} onClick={() => onAction(h, "primary")} className="col-span-2 h-11 bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50 sm:col-span-1" data-testid={`hypothesis-primary-${h.id}`}>
              <Check className="w-4 h-4 mr-1.5" /> {primary ? "Selected" : "Select as primary"}
            </Button>
            <Button disabled={busy} variant="outline" onClick={() => onAction(h, "save")} className="h-11 border-slate-200 text-slate-700 hover:bg-slate-50" data-testid={`hypothesis-save-${h.id}`}>
              <Bookmark className="w-4 h-4 mr-1.5" /> Save
            </Button>
            <Button disabled={busy} variant="outline" onClick={() => onAction(h, "reject")} className="h-11 border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600" data-testid={`hypothesis-reject-${h.id}`}>
              <X className="w-4 h-4 mr-1.5" /> Reject
            </Button>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default HypothesisDetail;
